// src/pages/TarotscopeSign.js
import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import Seo from "../components/seo";
// Adjust this import to match your project:
import CARDS from "../components/card-data"; // or: import { CARDS } from "../data/card-data";

const API_BASE = "https://tarotfate-api.tarotfate-com.workers.dev";

/* ===== Shared style tokens (matching your Tarotscope page) ===== */
const WRAP = "px-[1vw]";
const MAXW = "max-w-3xl mx-auto";
const FONT = "font-[Cormorant_Garamond]";
const TEXT = "text-white/90";

/* Page canvas (turq/plum ribbons) */
const CANVAS_BG = `
  radial-gradient(1200px 900px at 50% -12%, rgba(3,121,155,0.12), rgba(118, 159, 171, 0) 40%),
  linear-gradient(180deg, #014C75 10%, #005C83 50%, #016280ff 30%, #0C4B84 72%, #014C75 90%)
`;

/* Where your zodiac icons live */
const ICON_BASE = "/icons";

/* Signs with date ranges */
const SIGNS = [
  { name: "Aries", slug: "aries", dates: "Mar 21 – Apr 19" },
  { name: "Taurus", slug: "taurus", dates: "Apr 20 – May 20" },
  { name: "Gemini", slug: "gemini", dates: "May 21 – Jun 20" },
  { name: "Cancer", slug: "cancer", dates: "Jun 21 – Jul 22" },
  { name: "Leo", slug: "leo", dates: "Jul 23 – Aug 22" },
  { name: "Virgo", slug: "virgo", dates: "Aug 23 – Sep 22" },
  { name: "Libra", slug: "libra", dates: "Sep 23 – Oct 22" },
  { name: "Scorpio", slug: "scorpio", dates: "Oct 23 – Nov 21" },
  { name: "Sagittarius", slug: "sagittarius", dates: "Nov 22 – Dec 21" },
  { name: "Capricorn", slug: "capricorn", dates: "Dec 22 – Jan 19" },
  { name: "Aquarius", slug: "aquarius", dates: "Jan 20 – Feb 18" },
  { name: "Pisces", slug: "pisces", dates: "Feb 19 – Mar 20" },
];
const SIGN_BY_SLUG = Object.fromEntries(SIGNS.map(s => [s.slug, s]));

/* Tabs */
const TABS = ["yesterday", "today", "tomorrow"];
const TAB = "px-3 py-1 rounded-full border border-white/30 text-white/85 hover:bg-white/10 transition";
const TAB_ACTIVE = "bg-white/15 text-white border-white/50";

/* ------- card image + slug resolving from card-data.js ------- */
function pickImgField(obj) {
  return obj?.image || obj?.img || obj?.src || obj?.imageUrl || obj?.image_webp || null;
}
const NUM_WORDS = { zero:"0", one:"1", two:"2", three:"3", four:"4", five:"5", six:"6", seven:"7", eight:"8", nine:"9", ten:"10", page:"page", knight:"knight", queen:"queen", king:"king" };
const norm = s => (s || "").toLowerCase().replace(/\s+/g, " ").replace(/[’']/g,"'").trim();
function normalizeForCompare(name) {
  const n = norm(name);
  const m = n.match(/^(zero|one|two|three|four|five|six|seven|eight|nine|ten)\s+of\s+(.+)$/);
  if (m && NUM_WORDS[m[1]]) return `${NUM_WORDS[m[1]]} of ${m[2]}`;
  return n;
}
function getFromCards(cardName) {
  const target = normalizeForCompare(cardName);
  if (Array.isArray(CARDS)) {
    let found = CARDS.find(c => normalizeForCompare(c?.name || c?.title || "") === target);
    if (!found) found = CARDS.find(c => normalizeForCompare(c?.name || c?.title || "").includes(target));
    return found || null;
  }
  if (CARDS && typeof CARDS === "object") {
    for (const k of Object.keys(CARDS)) {
      const c = CARDS[k];
      if (normalizeForCompare(c?.name || c?.title || k) === target) return c;
    }
  }
  return null;
}
function getCardImage(cardName) {
  const found = getFromCards(cardName);
  return pickImgField(found) || null;
}
/** Try to get a canonical slug from data; otherwise fallback to a safe slugified name */
function getCardSlug(cardName) {
  const found = getFromCards(cardName);
  let slug = found?.slug || found?.urlSlug || found?.path || null;
  if (slug) return String(slug).replace(/^\/+|\/+$/g, "");
  // fallback slug from readable name
  return norm(cardName).replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");
}

export default function TarotscopeSign() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("today");
  const [err, setErr] = useState("");

  const signMeta = SIGN_BY_SLUG[slug] || { name: slug, dates: "" };
  const iconSrc = `${ICON_BASE}/tarotscope_${slug}.webp`;

  useEffect(() => {
    let cancelled = false;
    setErr(""); setData(null);
    (async () => {
      try {
        const r = await fetch(`${API_BASE}/api/tarotscope/${slug}`, { headers: { "Accept": "application/json" } });
        if (!r.ok) {
          const j = await r.json().catch(() => ({}));
          throw new Error(j?.message || `HTTP ${r.status}`);
        }
        const j = await r.json();
        if (!cancelled) setData(j);
      } catch (e) {
        if (!cancelled) setErr(String(e?.message || e));
      }
    })();
    return () => { cancelled = true; };
  }, [slug]);

  const entry = useMemo(() => (data ? data.entries?.[tab] || null : null), [data, tab]);
  const displayDate = useMemo(() => (data ? data.dates?.[tab] || "" : ""), [data, tab]);
  const cardImg = useMemo(() => (entry?.card ? getCardImage(entry.card) : null), [entry]);
  const cardSlug = useMemo(() => (entry?.card ? getCardSlug(entry.card) : null), [entry]);

  // SEO
  const pageTitle = `${signMeta.name} Tarotscope | TarotFate`;
  const pageDesc = `Read the ${signMeta.name} tarotscope for ${tab}. A grounded, practical message to guide your day.`;
  const canonical = `https://tarotfate.com/tarotscope/${slug}`;

  return (
    <div className={`${FONT} min-h-screen relative overflow-hidden text-white`} style={{ background: CANVAS_BG }}>
      <Seo
        title={pageTitle}
        description={pageDesc}
        url={canonical}
        image={`https://tarotfate.com/icons/tarotscope_${slug}.webp`}
        keywords={`${signMeta.name} tarotscope, ${signMeta.name} horoscope, daily tarotscope, tarot ${signMeta.name}`}
        schema={{
          "@context": "https://schema.org",
          "@type": "Article",
          headline: `${signMeta.name} Tarotscope`,
          about: `${signMeta.name} tarotscope daily message`,
          author: { "@type": "Organization", name: "TarotFate" },
          datePublished: displayDate || undefined,
          image: `https://tarotfate.com/icons/tarotscope_${slug}.webp`,
          mainEntityOfPage: canonical,
        }}
      />

      {/* ambient auras */}
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute -top-1/3 left-1/2 -translate-x-1/2 w-[1200px] h-[1200px] rounded-full opacity-25 blur-[160px] bg-[radial-gradient(circle,rgba(59,198,214,0.55)_0%,transparent_70%)]" />
        <div className="absolute -bottom-[25%] right-[5%] w-[700px] h-[700px] rounded-full opacity-20 blur-[140px] bg-[radial-gradient(circle,rgba(140,59,90,0.55)_0%,transparent_70%)]" />
      </div>

      {/* Header */}
      <header className={`${WRAP} ${MAXW} pt-8 pb-2 text-center relative z-[1]`}>
        <div className="flex justify-center">
          <Link
            to="/tarotscope"
            className="
              inline-flex items-center px-3 py-1 rounded-full
              text-[16px] tracking-[0.15em] uppercase
              bg-white/5 text-white/85 border border-white/15
              hover:bg-white/10 transition
            "
          >
            Tarotscope
          </Link>
        </div>

        <div className="mt-3 flex justify-center">
          <img
            src={iconSrc}
            alt={`${signMeta.name} icon`}
            width={96}
            height={96}
            loading="eager"
            decoding="async"
            draggable="false"
            className="w-24 h-24 object-contain select-none pointer-events-none"
            onContextMenu={(e) => e.preventDefault()}
          />
        </div>

        <h1 className="mt-2 text-3xl font-semibold">
          {signMeta.name}
          <span className="sr-only"> Tarotscope</span>
        </h1>

        {signMeta.dates && <p className="mt-1 text-white/80">{signMeta.dates}</p>}

        <div className="flex items-center justify-center mt-3" aria-hidden>
          <div className="flex-1 max-w-sm h-px bg-gradient-to-r from-transparent via-white/40 to-transparent" />
        </div>
      </header>

      {/* Body */}
      <main className={`${WRAP} ${MAXW} pb-16 relative z-[1]`}>
        {/* Tabs */}
        <div className="mt-4 flex justify-center gap-2">
          {TABS.map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`${TAB} ${tab === t ? TAB_ACTIVE : ""}`}
            >
              {t[0].toUpperCase() + t.slice(1)}
            </button>
          ))}
        </div>

        {/* Date */}
        <div className="mt-4 text-center">
          {displayDate ? (
            <p className="text-white text-base">{displayDate}</p>
          ) : (
            <p className="text-white/50 text-sm">Loading date…</p>
          )}
        </div>

        {/* Content */}
        <section className="mt-6">
          {!data && !err && (
            <div className="text-white/80 text-center">Loading Tarotscope…</div>
          )}
          {err && (
            <div className="text-red-200/90 text-center">Could not load Tarotscope at this moment... {err}</div>
          )}

          {data && !entry && (
            <div className="text-white/80 text-center">
              {tab === "tomorrow"
                ? "Tomorrow’s tarotscope will appear once it’s generated."
                : "No entry found for this day."}
            </div>
          )}

          {entry && (
            <div
              className="
                mt-4 rounded-2xl overflow-hidden
                bg-gradient-to-b from-white/5 via-[#14656a]/25 to-[#0f3d3f]/10
                border border-white/10
              "
            >
              {/* Reading content */}
              <div className="p-4 sm:p-5 md:p-6">
                <div className="flex flex-col md:flex-row md:items-start md:gap-5">
                  {/* Message */}
                  <article className="order-1 md:order-2 md:pr-2">
                    <p className="text-base sm:text-base leading-relaxed text-white break-words">
                      {entry.message}
                    </p>
                  </article>

                  {/* Card image */}
                  <div className="order-2 md:order-1 mt-4 md:mt-0 md:mb-0 md:shrink-0 md:self-start">
                    {cardImg ? (
                      cardSlug ? (
                        <Link
                          to={`/tarot-card/${cardSlug}`}
                          className="block focus:outline-none focus-visible:ring-2 focus-visible:ring-white/70 rounded-lg"
                          aria-label={entry.card ? `${entry.card} meaning` : "Tarot card meaning"}
                        >
                          <img
                            src={cardImg}
                            alt={entry.card ? `${entry.card} tarot card` : "Tarot card"}
                            className="w-36 sm:w-40 md:w-32 h-auto rounded-lg ring-1 ring-white/15 shadow-md object-contain mx-auto md:mx-0 select-none pointer-events-none"
                            loading="lazy"
                            decoding="async"
                            draggable="false"
                            onContextMenu={(e) => e.preventDefault()}
                          />
                        </Link>
                      ) : (
                        <img
                          src={cardImg}
                          alt={entry.card ? `${entry.card} tarot card` : "Tarot card"}
                          className="w-36 sm:w-40 md:w-32 h-auto rounded-lg ring-1 ring-white/15 shadow-md object-contain mx-auto md:mx-0 select-none pointer-events-none"
                          loading="lazy"
                          decoding="async"
                          draggable="false"
                          onContextMenu={(e) => e.preventDefault()}
                        />
                      )
                    ) : (
                      cardSlug ? (
                        <Link
                          to={`/tarot-card/${cardSlug}`}
                          className="block focus:outline-none focus-visible:ring-2 focus-visible:ring-white/70 rounded-lg"
                          aria-label={entry.card ? `${entry.card} meaning` : "Tarot card meaning"}
                        >
                          <div
                            className="w-36 sm:w-40 md:w-32 aspect-[5/8] rounded-lg ring-1 ring-white/15 shadow-md bg-white/10 grid place-items-center text-white/60 text-xs mx-auto md:mx-0"
                            aria-label="Card image not available"
                          >
                            Card
                            <span className="sr-only">{entry.card}</span>
                          </div>
                        </Link>
                      ) : (
                        <div
                          className="w-36 sm:w-40 md:w-32 aspect-[5/8] rounded-lg ring-1 ring-white/15 shadow-md bg-white/10 grid place-items-center text-white/60 text-xs mx-auto md:mx-0"
                          aria-label="Card image not available"
                        >
                          Card
                          <span className="sr-only">{entry.card}</span>
                        </div>
                      )
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Separator (always shown) */}
          <div className="mt-10 flex items-center justify-center">
            <div className="w-3/4 h-px bg-gradient-to-r from-transparent via-white/25 to-transparent" />
          </div>

          {/* Other signs section (always rendered) */}
          <div className="mt-8 text-center text-white/85">
            <p className="mb-4 text-base">
              Explore more <Link to="/tarotscope" className="underline hover:text-white">tarotscopes</Link> for other zodiac signs:
            </p>
            <div className="flex flex-wrap justify-center gap-3">
              {SIGNS.filter(s => s.slug !== slug).map(sign => (
                <Link
                  key={sign.slug}
                  to={`/tarotscope/${sign.slug}`}
                  className="px-3 py-1 rounded-full border border-white/20 text-white/80 hover:bg-white/10 hover:text-white transition text-sm"
                >
                  {sign.name}
                </Link>
              ))}
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}
